import { create } from "zustand";
import axios from "axios";
/*import { AccessToken } from "@okta/okta-auth-js";*/
import IReturnResults from "common/interfaces/IReturnResults";
import { getAxiosErrorReturnResults } from "common/utils/errorHandling";

export interface IProject {
  projectID: number;
  projectName: string;
  updatedDate: Date;
  createdDate: Date;
  networkCount: number;
  rowVersion: string;
  sharedUserID: number;
  sharedRowVersion: string;
  owner: string;
}

export interface IProjectShare {
  projectID: number;
  users: ISharedUser[];
}
export interface ISharedUser {
  userID: number;
  nameIdentifier: string;
  rowVersion: string;
}

interface IFetchProjectShareResults extends IReturnResults {
  projectShare: IProjectShare | null;
}

interface IShareProjectResults extends IReturnResults {
  user: ISharedUser | null;
}

type UserStore = {
  projects: IProject[];
    fetchProjects: (apiBaseUrl: string, getAccessToken: () => Promise<string | null>) => Promise<IReturnResults>;
  deleteProject: (
    apiBaseUrl: string,
      getAccessToken: () => Promise<string | null>,
    project: IProject,
  ) => Promise<IReturnResults>;
  renameProject: (
    apiBaseUrl: string,
      getAccessToken: () => Promise<string | null>,
    project: IProject,
    newName: string,
  ) => Promise<IReturnResults>;
  copyProject: (
    apiBaseUrl: string,
      getAccessToken: () => Promise<string | null>,
    project: IProject,
  ) => Promise<IReturnResults>;
  fetchProjectShare: (
    apiBaseUrl: string,
      getAccessToken: () => Promise<string | null>,
    projectID: number,
  ) => Promise<IFetchProjectShareResults>;
  shareProject: (
    apiBaseUrl: string,
      getAccessToken: () => Promise<string | null>,
    projectID: number,
    email: string
  ) => Promise<IShareProjectResults>;
  unshareProject: (
    apiBaseUrl: string,
      getAccessToken: () => Promise<string | null>,
    projectID: number,
    userID: number,
    rowVersion: string
  ) => Promise<IReturnResults>;
  isBusy: boolean;
};

const useUserStore = create<UserStore>((set) => ({
  isBusy: false,
  hasError: false,
  projects: [],
  fetchProjects: async (apiBaseUrl, getAccessToken) => {
    try {
      set((state) => ({ isBusy: true }));

      const accessToken = await getAccessToken();
      if (accessToken === null) {
        set((state) => ({ isBusy: false }));
        return { succeeded: false, errorMessage: "Unable to retrieve authorized access token." };
      }

      const response = await axios.get<{ projects: IProject[] }>(`${apiBaseUrl}/api/project/projects`, {
           headers: { Authorization: `Bearer ${accessToken}` },
          //headers: {
          //    "Authorization": `Bearer ${accessToken}`,
          //    "Content-Type": "application/json"
          //}

      });
      const groupedProjects: IProject[] = response.data.projects;
      set((state) => ({
        isBusy: false,
        projects: groupedProjects.map((proj) => ({
          ...proj,
          owner: proj.sharedUserID > 0 ? "tbl_proj_owner_shared" : "tbl_proj_owner_private",
        })),
      }));
      console.log(response.data.projects);
      return { succeeded: true, errorMessage: null };
    } catch (err) {
      set((state) => ({ isBusy: false }));
      console.log("fetchProjects threw an error", err);
      return getAxiosErrorReturnResults(err);
    }
  },

  deleteProject: async (apiBaseUrl, getAccessToken, project) => {
    set((state) => ({ isBusy: true }));
    try {
      const accessToken = await getAccessToken();
      if (accessToken === null) {
        set((state) => ({ isBusy: false }));
        return { succeeded: false, errorMessage: "Unable to retrieve authorized access token." };
      }

      await axios.post(
        `${apiBaseUrl}/api/project/delete`,
        {
          projectID: project.projectID,
          rowVersion: project.rowVersion,
        },
        {
            headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      // Now update the list of projects, just to get the name/rowversion updated
      set((state) => {
        const modifiedProjects = state.projects.filter((proj) => proj.projectID !== project.projectID);
        return { projects: modifiedProjects, isBusy: false };
      });
      return { succeeded: true, errorMessage: null };
    } catch (err: unknown) {
      set((state) => ({ isBusy: false }));
      console.log("deleteProject threw an error", err);
      return getAxiosErrorReturnResults(err);
    }
  },

  renameProject: async (apiBaseUrl, getAccessToken, project, newName) => {
    set((state) => ({ isBusy: true }));
    try {
      const accessToken = await getAccessToken();
      if (accessToken === null) {
        set((state) => ({ isBusy: false }));
        return { succeeded: false, errorMessage: "Unable to retrieve authorized access token." };
      }

      const response = await axios.post(
        `${apiBaseUrl}/api/project/rename`,
        {
          projectID: project.projectID,
          projectName: newName,
          rowVersion: project.rowVersion,
        },
        {
            headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      // Now update the list of projects, just to get the name/rowversion updated
      set((state) => {
        const modifiedProjects = state.projects.map((proj) => {
          if (proj.projectID === project.projectID) {
            return {
              ...proj,
              projectName: newName,
              rowVersion: response.data.rowVersion,
            };
          } else {
            return proj;
          }
        });
        return { projects: modifiedProjects, isBusy: false };
      });
      return { succeeded: true, errorMessage: null };
    } catch (err: unknown) {
      set((state) => ({ isBusy: false }));
      console.log("renameProject threw an error", err);
      return getAxiosErrorReturnResults(err);
    }
  },

  copyProject: async (apiBaseUrl, getAccessToken, project) => {
    set((state) => ({ isBusy: true }));
    try {
      const accessToken = await getAccessToken();
      if (accessToken === null) {
        set((state) => ({ isBusy: false }));
        return { succeeded: false, errorMessage: "Unable to retrieve authorized access token." };
      }

      const response = await axios.post(
        `${apiBaseUrl}/api/project/copy`,
        {
          projectID: project.projectID,
        },
        {
            headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      // Now update the list of projects, just to get the name/rowversion updated
      set((state) => ({ projects: [...state.projects, response.data], isBusy: false }));
      return { succeeded: true, errorMessage: null };
    } catch (err: unknown) {
      set((state) => ({ isBusy: false }));
      console.log("copyProject threw an error", err);
      return getAxiosErrorReturnResults(err);
    }
  },

  fetchProjectShare: async (apiBaseUrl, getAccessToken, projectID) => {
    try {
      set((state) => ({ isBusy: true }));

      const accessToken = await getAccessToken();
      if (accessToken === null) {
        set((state) => ({ isBusy: false }));
        return { succeeded: false, errorMessage: "Unable to retrieve authorized access token.", projectShare: null };
      }

      const response = await axios.get<{ users: ISharedUser[] }>(`${apiBaseUrl}/api/project/sharedUsers?projectID=${projectID}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
      });
      const projectShare: IProjectShare = {
        projectID: projectID,
        users: response.data.users,
      };
      set((state) => ({
        isBusy: false,
      }));
      console.log(projectShare);
      return { succeeded: true, errorMessage: null, projectShare: projectShare };
    } catch (err) {
      set((state) => ({ isBusy: false }));
      console.log("fetchProjectShare threw an error", err);
      return {
        ...getAxiosErrorReturnResults(err),
        projectShare: null,
      };
    }
  },

  shareProject: async (apiBaseUrl, getAccessToken, projectID, email) => {
    try {
      set((state) => ({ isBusy: true }));

      const accessToken = await getAccessToken();
      if (accessToken === null) {
        set((state) => ({ isBusy: false }));
        return { succeeded: false, errorMessage: "Unable to retrieve authorized access token.", user: null };
      }

      const response = await axios.post(
        `${apiBaseUrl}/api/project/share`,
        {
          projectID: projectID,
          emailAddress: email,
        },
        {
            headers: { Authorization: `Bearer ${accessToken}` },
        },
      );

      set((state) => ({
        isBusy: false,
      }));
      console.log("shareProject", response.data);
      return { succeeded: true, errorMessage: null, user: {...response.data} };
    } catch (err) {
      set((state) => ({ isBusy: false }));
      console.log("shareProject threw an error", err);
      return {
        ...getAxiosErrorReturnResults(err),
        user: null
      };
    }
  },

  unshareProject: async (apiBaseUrl, getAccessToken, projectID, userID, rowVersion) => {
    try {
      set((state) => ({ isBusy: true }));

      const accessToken = await getAccessToken();
      if (accessToken === null) {
        set((state) => ({ isBusy: false }));
        return { succeeded: false, errorMessage: "Unable to retrieve authorized access token." };
      }

      await axios.post(
        `${apiBaseUrl}/api/project/unshare`,
        {
          projectID: projectID,
          userID: userID,
          rowVersion: rowVersion
        },
        {
            headers: { Authorization: `Bearer ${accessToken}` },
        },
      );

      set((state) => ({
        isBusy: false,
      }));
      return { succeeded: true, errorMessage: null };
    } catch (err) {
      set((state) => ({ isBusy: false }));
      console.log("unshareProject threw an error", err);
      return getAxiosErrorReturnResults(err);
    }
  },

}));

export default useUserStore;
